import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Flame, Zap, X } from 'lucide-react';
import { commandCenterApi } from '../services/api';

// Celebratory banner that congratulates recruiters when they hit a daily milestone.
// Triggers are tiered so the message escalates with effort:
//   - Bronze (warm-up):  3+ submissions today
//   - Silver (face-time): 1+ interview today
//   - Gold (hat-trick):   3+ submissions AND 1+ interview today
// Dismissible per-day via localStorage; auto re-appears tomorrow.
// Reads from the `metrics` object already in CommandCenter — no extra API calls.
const TodaysWinBanner = ({ metrics, user }) => {
  const sub = Number(metrics?.submissions_today || 0);
  const iv = Number(metrics?.interviews_today || 0);
  const rangeOn = !!metrics?.range_active;
  const isRecruiter = user?.role === 'recruiter';

  // Stable storage key — only one dismissal per recruiter per day per tier
  const tier = useMemo(() => {
    if (sub >= 3 && iv >= 1) return 'gold';
    if (iv >= 1) return 'silver';
    if (sub >= 3) return 'bronze';
    return null;
  }, [sub, iv]);

  const storageKey = useMemo(
    () => `cc-todays-win-${new Date().toDateString()}-${tier || 'none'}`,
    [tier]
  );

  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined' || !tier) return false;
    return localStorage.getItem(storageKey) === '1';
  });

  // Slack celebration: fire-and-forget POST when GOLD tier is reached for the
  // first time today. Backend enforces idempotency via a unique daily _id, so
  // even if the user reloads we don't spam the channel. Ref guards against
  // duplicate fires within the same React render cycle.
  const celebrateFiredRef = useRef(false);
  useEffect(() => {
    if (!isRecruiter || rangeOn) return;
    if (tier !== 'gold') return;
    if (celebrateFiredRef.current) return;
    const slackKey = `cc-todays-win-slack-${new Date().toDateString()}`;
    if (typeof window !== 'undefined' && localStorage.getItem(slackKey) === '1') return;
    celebrateFiredRef.current = true;
    commandCenterApi
      .celebrateWin({
        tier: 'on_fire',
        submissions_today: sub,
        interviews_today: iv,
      })
      .then(() => {
        try { localStorage.setItem(slackKey, '1'); } catch { /* quota — ignore */ }
      })
      .catch(() => {
        // Silent. Backend may 400/403/422 in edge cases; never block the UI.
        celebrateFiredRef.current = false;
      });
  }, [tier, isRecruiter, rangeOn, sub, iv]);

  // Hide for:
  // - non-recruiters (managers don't need personal pat-on-back; they have their own dashboards)
  // - custom date range mode (KPIs become aggregate, not "today")
  // - no tier reached
  // - dismissed today
  if (!isRecruiter || rangeOn || !tier || dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* quota — ignore */ }
    setDismissed(true);
  };

  const config = {
    bronze: {
      Icon: Sparkles,
      title: `Solid pace — ${sub} submissions in the bag today`,
      sub: 'Keep the rhythm going. One interview locked-in would push this to a Big Day.',
      wrap: 'bg-gradient-to-r from-sky-50 via-cyan-50 to-sky-50 border-sky-200 text-sky-900',
      pill: 'bg-sky-500',
      label: 'WARM-UP',
    },
    silver: {
      Icon: Zap,
      title: `Big day — ${iv} interview${iv === 1 ? '' : 's'} on the calendar`,
      sub:
        sub > 0
          ? `Plus ${sub} fresh submission${sub === 1 ? '' : 's'}. Every interview is a closure waiting to happen.`
          : 'Lock-in interviews drive the pipeline. Add a couple of submissions to round it off.',
      wrap: 'bg-gradient-to-r from-violet-50 via-indigo-50 to-violet-50 border-violet-200 text-violet-900',
      pill: 'bg-violet-500',
      label: 'BIG DAY',
    },
    gold: {
      Icon: Flame,
      title: `Hat-trick! ${sub} submissions + ${iv} interview${iv === 1 ? '' : 's'} today`,
      sub: "This is the kind of day that lands offers next week. Keep pushing — the streak is real.",
      wrap: 'bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border-amber-300 text-amber-900',
      pill: 'bg-amber-500',
      label: 'ON FIRE',
    },
  }[tier];

  const Icon = config.Icon;

  return (
    <div
      className={`relative flex items-center gap-3 px-3 py-2 rounded-lg border ${config.wrap} shadow-sm`}
      data-testid="todays-win-banner"
      data-tier={tier}
      role="status"
    >
      <div className="w-8 h-8 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded text-white ${config.pill}`}
            data-testid="todays-win-tier"
          >
            {config.label}
          </span>
          <div className="text-sm font-semibold truncate" data-testid="todays-win-title">
            {config.title}
          </div>
        </div>
        <div className="text-[11px] text-slate-600 mt-0.5 truncate" data-testid="todays-win-sub">
          {config.sub}
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss for today"
        className="text-slate-400 hover:text-slate-700 flex-shrink-0"
        data-testid="todays-win-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default TodaysWinBanner;
